import {
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Text,
} from "@chakra-ui/react";
import ModalDrawer from "@components/ModalDrawer";
import { XIcon as IoClose } from "@phosphor-icons/react";
import BaseImage from "@components/BaseImage";
import { LSTIconMapping, TXN_TYPES } from "@constants/common";
import { useState } from "react";
import { ReactNode } from "react";
import useDappChain from "@hooks/useDappChain";

export enum TransactionType {
  APPROVAL = "APPROVAL",
  DEPOSIT = "DEPOSIT",
}

const TRANSACTIONS_KEYS = {
  [TXN_TYPES.VAULT_DEPOSIT]: "Deposit",
  [TXN_TYPES.YIELD_VAULT_DEPOSIT]: "Deposit",
  [TXN_TYPES.STABLE_VAULT_DEPOSIT]: "Deposit",
  [TXN_TYPES.VAULT_WITHDRAW]: "Withdraw",
  [TXN_TYPES.YIELD_VAULT_WITHDRAW]: "Withdraw",
  [TXN_TYPES.STABLE_VAULT_WITHDRAW]: "Withdraw",
  [TXN_TYPES.STABLE_VAULT_CLAIM]: "Claim",
  [TXN_TYPES.STAKE]: "Restake",
  [TXN_TYPES.UNSTAKE]: "Unstake",
  [TXN_TYPES.CRYPTANIUM_VAULT_DEPOSIT]: "Deposit",
  [TXN_TYPES.CRYPTANIUM_VAULT_WITHDRAW]: "Withdraw",
  [TXN_TYPES.CRYPTANIUM_VAULT_CLAIM]: "Claim",
  [TXN_TYPES.WRAP]: "wrap",
  [TXN_TYPES.UNWRAP]: "unwrap",
};

interface ReviewModalProps {
  txModalTitle?: ReactNode;
  transaction: {
    from: string;
    to: string;
    amountIn: string;
    amountOut: string;
    interactionPool?: string;
  };
  visible: boolean;
  flags: {
    isVault: boolean;
    isUnstake: boolean;
    isYieldVault?: boolean;
    isStableVault?: boolean;
    isCryptaniumVault?: boolean;
    isClaim?: boolean;
    isWrap?: boolean;
  };
  closeModal: () => void;
  onConfirm: () => void;
}

const ReviewModal = (props: ReviewModalProps) => {
  const { transaction, txModalTitle, flags, closeModal, onConfirm } = props;
  const [isChecked, setIsChecked] = useState(false);
  const { chain } = useDappChain();

  const transactionType = (() => {
    if (flags.isVault) {
      return flags.isUnstake
        ? flags.isClaim && flags.isStableVault
          ? TXN_TYPES.STABLE_VAULT_CLAIM
          : flags.isClaim && flags.isCryptaniumVault
            ? TXN_TYPES.CRYPTANIUM_VAULT_CLAIM
            : flags.isStableVault
              ? TXN_TYPES.STABLE_VAULT_WITHDRAW
              : flags.isCryptaniumVault
                ? TXN_TYPES.CRYPTANIUM_VAULT_WITHDRAW
                : flags.isYieldVault
                  ? TXN_TYPES.YIELD_VAULT_WITHDRAW
                  : TXN_TYPES.VAULT_WITHDRAW
        : flags.isStableVault
          ? TXN_TYPES.STABLE_VAULT_DEPOSIT
          : flags.isCryptaniumVault
            ? TXN_TYPES.CRYPTANIUM_VAULT_DEPOSIT
            : flags.isYieldVault
              ? TXN_TYPES.YIELD_VAULT_DEPOSIT
              : TXN_TYPES.VAULT_DEPOSIT;
    }
    return flags.isUnstake ? TXN_TYPES.UNSTAKE : TXN_TYPES.STAKE;
  })();

  const handleConfirm = () => {
    if (isChecked) {
      //   closeModal();
      onConfirm();
    }
  };

  if (!props.visible) {
    return null;
  }

  return (
    <ModalDrawer
      isOpen={true}
      closeAlert={closeModal}
      closeOnOverlayClick={false}
      maxH="75%"
      bodyProps={{ overflow: "hidden" }}
    >
      <Flex
        direction="column"
        gap="24px"
        padding="24px 32px"
        width={{ base: "100%", md: "548px" }}
        maxWidth={{ base: "100%", md: "548px" }}
      >
        <Flex alignItems="center" justifyContent="space-between">
          {txModalTitle ? (
            txModalTitle
          ) : (
            <Text
              fontSize="16px"
              lineHeight="24px"
              fontWeight={500}
              color="#1A1A1A"
            >
              Review {TRANSACTIONS_KEYS[transactionType]}
            </Text>
          )}

          <IconButton
            icon={<IoClose size={16} color="#075A5A" weight="regular" />}
            aria-label="Close Icon"
            backgroundColor="transparent"
            _hover={{ backgroundColor: "transparent" }}
            onClick={closeModal}
            border="1px solid #EEEEEF"
            borderRadius="16px"
            className="!w-8 !h-auto !aspect-square"
          />
        </Flex>

        <Box padding="0" borderRadius="1rem">
          <Flex
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
            gap="8px"
            width="100%"
          >
            <Flex
              justifyContent="space-between"
              alignItems={{ base: "start", md: "center" }}
              gap={{ base: "0.5rem", md: "1rem" }}
              direction={{ base: "row", md: "row" }}
              width="100%"
            >
              <Flex
                gap="6px"
                justifyContent="center"
                alignItems="center"
                bg="#F4F4F7"
                borderRadius="16px"
                padding="24px"
                width="50%"
                minWidth="130px"
                maxWidth="250px"
              >
                <Box width="max-content">
                  <BaseImage
                    src={LSTIconMapping[transaction.from]}
                    width={24}
                    height={24}
                    alt={transaction.from}
                  />
                </Box>
                <Flex
                  alignItems={{ base: "start", md: "center" }}
                  direction="column"
                  gap="2px"
                  width="max-content"
                >
                  <Text
                    color="#4A6A6A"
                    fontSize="10px"
                    lineHeight="12px"
                    fontWeight="400"
                    align="left"
                  >
                    You pay
                  </Text>
                  <Text
                    fontSize="16px"
                    lineHeight="24px"
                    color="#1A1A1A"
                    fontWeight={500}
                    width="max-content"
                  >
                    {transaction.amountIn} {transaction.from}
                  </Text>
                </Flex>
              </Flex>
              <Flex
                gap="6px"
                justifyContent="center"
                alignItems="center"
                bg="#F4F4F7"
                borderRadius="16px"
                padding="24px"
                width="50%"
                minWidth="130px"
                maxWidth="250px"
              >
                <Box width="max-content">
                  <BaseImage
                    src={LSTIconMapping[transaction.to]}
                    width={24}
                    height={24}
                    alt={transaction.to}
                  />
                </Box>
                <Flex
                  alignItems={{ base: "start", md: "center" }}
                  direction="column"
                  gap="2px"
                  width="max-content"
                >
                  <Text
                    color="#4A6A6A"
                    fontSize="10px"
                    lineHeight="12px"
                    fontWeight="400"
                    align="left"
                  >
                    You get
                  </Text>
                  <Text
                    fontSize="16px"
                    lineHeight="24px"
                    color="#1A1A1A"
                    fontWeight={500}
                    width="max-content"
                  >
                    {transaction.amountOut} {transaction.to}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
            {transaction?.interactionPool && (
              <Box
                bg="#FFF4E6"
                borderRadius="12px"
                padding="12px 16px"
                marginTop="12px"
                width="100%"
                border="1px solid #FFE5B4"
              >
                <Flex gap="8px" alignItems="flex-start">
                  <Checkbox
                    isChecked={isChecked}
                    onChange={(e) => setIsChecked(e.target.checked)}
                    colorScheme="green"
                    size="lg"
                    bg="#FFF"
                  />

                  <Flex
                    gap="8px"
                    flexDirection="column"
                    alignItems="flex-start"
                    flex="1"
                  >
                    <Text
                      fontSize="12px"
                      lineHeight="16px"
                      fontWeight={400}
                      color="#4A6A6A"
                    >
                      I will verify my wallet interacts only with the contract
                    </Text>
                    <Text
                      fontSize="12px"
                      lineHeight="16px"
                      fontWeight={500}
                      color="#1A1A1A"
                      wordBreak="break-all"
                    >
                      {chain.name}: {transaction?.interactionPool}
                    </Text>
                  </Flex>
                </Flex>
              </Box>
            )}
          </Flex>
        </Box>

        <Flex direction="column" gap="16px" alignItems="center" width="100%">
          <Button
            backgroundColor="#075A5A"
            color="white"
            _hover={{
              backgroundColor: "#075A5A",
              color: "white",
            }}
            _disabled={{
              backgroundColor: "#E0E0E0",
              color: "#A0A0A0",
              cursor: "not-allowed",
            }}
            alignSelf="center"
            borderRadius="0.5rem"
            onClick={handleConfirm}
            isDisabled={!isChecked}
            width="100%"
          >
            Confirm
          </Button>
        </Flex>
      </Flex>
    </ModalDrawer>
  );
};

export default ReviewModal;
